'use client'

import { useState, useCallback } from 'react'
import Cropper from 'react-easy-crop'
import {
  Button,
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  Row,
  Col,
  Icon,
  Slider,
} from '@repo/ui/components'
import { useTranslate } from '@repo/shared/hooks'
import getCroppedImg from './utils'

interface ImageCropperProps {
  open: boolean
  onClose: () => void
  imageSrc: string | null
  onCropComplete: (croppedImage: File) => void
  aspect?: number
  cropShape?: 'rect' | 'round'
}

export const ImageCropper = ({
  open,
  onClose,
  imageSrc,
  onCropComplete,
  aspect = 1,
  cropShape = 'round',
}: ImageCropperProps) => {
  const { t } = useTranslate()
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [rotation, setRotation] = useState(0)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<{
    width: number
    height: number
    x: number
    y: number
  } | null>(null)
  const [isCropping, setIsCropping] = useState(false)

  const onCropChange = useCallback((crop: { x: number; y: number }) => {
    setCrop(crop)
  }, [])

  const onZoomChange = useCallback((zoom: number) => {
    setZoom(zoom)
  }, [])

  const onRotationChange = useCallback((rotation: number) => {
    setRotation(rotation)
  }, [])

  const onCropAreaChange = useCallback(
    (
      croppedArea: { width: number; height: number; x: number; y: number },
      croppedAreaPixels: {
        width: number
        height: number
        x: number
        y: number
      },
    ) => {
      setCroppedAreaPixels(croppedAreaPixels)
    },
    [],
  )

  const handleCropComplete = useCallback(async () => {
    if (!imageSrc || !croppedAreaPixels) return

    setIsCropping(true)
    try {
      const croppedImage = await getCroppedImg(
        imageSrc,
        croppedAreaPixels,
        rotation,
      )
      onCropComplete(croppedImage)
      onClose()
    } catch (error) {
      console.error('Error cropping image:', error)
    } finally {
      setIsCropping(false)
    }
  }, [imageSrc, croppedAreaPixels, rotation, onCropComplete, onClose])

  const handleClose = useCallback(() => {
    setCrop({ x: 0, y: 0 })
    setZoom(1)
    setRotation(0)
    setCroppedAreaPixels(null)
    onClose()
  }, [onClose])

  if (!imageSrc) return null

  return (
    <Modal open={open} onOpenChange={handleClose}>
      <ModalContent className='w-full max-h-[90vh] overflow-y-auto'>
        <ModalHeader>
          <ModalTitle>{t('imageCropper_title')}</ModalTitle>
        </ModalHeader>

        <Col className='p-md md:p-lg space-y-md md:space-y-lg'>
          {/* Crop Area */}
          <div className='relative h-64 md:h-96 bg-neutral-surface-20 rounded-lg overflow-hidden'>
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              rotation={rotation}
              cropShape={cropShape}
              onCropChange={onCropChange}
              onZoomChange={onZoomChange}
              onRotationChange={onRotationChange}
              onCropAreaChange={onCropAreaChange}
              showGrid={false}
            />
          </div>

          {/* Controls */}
          <Col className='space-y-sm md:space-y-md'>
            {/* Zoom Control */}
            <Row className='items-center gap-sm md:gap-md'>
              <Icon
                name='Plus'
                size='sm'
                className='text-neutral-surface-60 flex-shrink-0'
              />
              <Slider
                value={[zoom]}
                onValueChange={(value) => setZoom(value[0] ?? 1)}
                min={1}
                max={3}
                step={0.1}
                className='flex-1'
              />
              <span className='text-xs md:text-sm text-neutral-surface-60 min-w-10 md:min-w-12'>
                {zoom.toFixed(1)}x
              </span>
            </Row>

            {/* Rotation Control */}
            <Row className='items-center gap-sm md:gap-md'>
              <Icon
                name='ArrowRight'
                size='sm'
                className='text-neutral-surface-60 flex-shrink-0'
              />
              <Slider
                value={[rotation]}
                onValueChange={(value) => setRotation(value[0] ?? 0)}
                min={0}
                max={360}
                step={1}
                className='flex-1'
              />
              <span className='text-xs md:text-sm text-neutral-surface-60 min-w-10 md:min-w-12'>
                {rotation}°
              </span>
            </Row>
          </Col>

          {/* Action Buttons */}
          <Row className='justify-end gap-sm md:gap-md flex-wrap'>
            <Button
              variant='ghost'
              color='neutral'
              onClick={handleClose}
              disabled={isCropping}
              size='sm'
              className='flex-1 md:flex-none'
            >
              {t('common_cancel')}
            </Button>
            <Button
              variant='fill'
              color='base'
              onClick={handleCropComplete}
              loading={isCropping}
              size='sm'
              className='flex-1 md:flex-none'
            >
              {t('imageCropper_apply')}
            </Button>
          </Row>
        </Col>
      </ModalContent>
    </Modal>
  )
}
